import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../composants/Button/Button';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import OperationConsultationSuivi from './OperationConsultationSuivi';
import { StatusId, OperationMS } from '../types';
import { statusColorById } from '../domain/operationStatus';
import { analyseNextTab } from '../domain/nextTab';
import {
  isAnalyseViewOnly,
  isConsultationEnCours,
  isAnalyseCloturee,
} from '../domain/analyse/accessAnalyse';
import { canAnnulerOperation } from '../domain/operationStatus';
import { validateNotation, NotationErrors, computeNoteGlobale } from '../domain/analyse/notationTechnique';
import { resolveWinnerId } from '../domain/analyse/attribution';
import { buildNotePayload, buildValidatePayload } from '../domain/analyse/notePayload';
import { isAnalyzed, getDepotParCommande, getRefusParCommande } from '../domain/analyse/historique';
import { CommandeAnalyse } from '../domain/analyse/types';
import { saveNotesTechniques, finaliserAnalyse, annulerOperation } from '../services/operationMsService';
import { useNotesTechniques } from '../hooks/useNotesTechniques';
import { useAnalyseDechiffrage } from '../hooks/useAnalyseDechiffrage';
import { useAnalyseIa } from '../hooks/useAnalyseIa';
import { useSignataires } from '../hooks/useSignataires';
import OperationAnalyseAccordion from './OperationAnalyseAccordion';
import OperationStatusBanner from './OperationStatusBanner';
import OperationAnalyseReception from './OperationAnalyseReception';
import OperationAnalyseTechnique from './OperationAnalyseTechnique';
import OperationAnalyseIaButton from './OperationAnalyseIaButton';
import OperationAnalyseSynthese from './OperationAnalyseSynthese';
import OperationAnalyseFinanciere from './OperationAnalyseFinanciere';
import { pdf } from '@react-pdf/renderer';
import AnalysePrestatairePDF from '../../PDF/AnalysePrestatairePDF';
import { useAuth } from '../../../contexts/AuthContext';
import { buildFinancialPool, resolveNoteFinanciere } from '../domain/analyse/scoring';
import { scrollToTop } from '../../../utils/scroll';

interface OperationAnalyseProps {
  operation: OperationMS;
  setOperation: React.Dispatch<React.SetStateAction<OperationMS | null>>;
  refetch: () => void;
  printRef: React.RefObject<HTMLElement>;
}

const OperationAnalyse: React.FC<OperationAnalyseProps> = ({
  operation,
  setOperation,
  refetch,
  printRef,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { techNotes, setNote } = useNotesTechniques(operation);
  const {
    info: dechiffrageInfo,
    plisOuverts,
    dechiffrer,
    error: dechiffrageError,
    loading: dechiffrageLoading,
  } = useAnalyseDechiffrage(operation, refetch);
  // Analyse IA des offres (async) : declenchee a la demande via le bouton de la section
  // "Analyse des offres techniques" (independante du dechiffrage), une seule fois.
  const { etat: iaEtat, lancer: lancerIa } = useAnalyseIa(operation, refetch);
  // Signataire designe (auto), consomme en silence par le payload de cloture (pas d'UI de choix).
  const { selection } = useSignataires(
    operation.organisme?.id,
    operation.id_signataire,
    operation.type_signataire,
  );

  // Le retenu est le MIEUX-DISANT (note globale), calcule par resolveWinnerId. Pas de selection manuelle.
  const [notationErrors, setNotationErrors] = useState<NotationErrors>({});

  // Etat des accordeons (les sections d'analyse s'ouvrent au dechiffrage).
  const [recOpen, setRecOpen] = useState(true);
  const [contenuOpen, setContenuOpen] = useState(true);
  const [analyseOpen, setAnalyseOpen] = useState(plisOuverts);
  const [financiereOpen, setFinanciereOpen] = useState(plisOuverts);
  const [syntheseOpen, setSyntheseOpen] = useState(plisOuverts);

  // Modales + drapeaux d'action.
  const [isAnnulerOpen, setIsAnnulerOpen] = useState(false);
  const [isClotureOpen, setIsClotureOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);
  const [isAnnuling, setIsAnnuling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const statusId = operation.status?.id;
  const commandes = (operation.commandes ?? []) as CommandeAnalyse[];

  // Genere + telecharge le PDF de l'offre/analyse d'un candidat (bouton "Offre du candidat"
  // du Contenu des plis, visible une fois les plis dechiffres). Pont vers le composant PDF
  // legacy AnalysePrestatairePDF (props any-typees) : on adapte les 2 fonctions attendues
  // a partir des domaines V2 (scoring financier + note technique globale).
  const handlePrintOffre = useCallback(
    async (commandeId: number | string) => {
      const calculateFinancialScore = (prestataires: CommandeAnalyse[]): { [id: string]: number } => {
        const pool = buildFinancialPool(prestataires);
        const map: { [id: string]: number } = {};
        prestataires.forEach((p) => {
          map[String(p.id)] = resolveNoteFinanciere(p, pool);
        });
        return map;
      };
      const getNoteGlobale = (_tn: unknown, prestaIdStr: string): string =>
        String(computeNoteGlobale(techNotes[prestaIdStr]));

      const blob = await pdf(
        <AnalysePrestatairePDF
          commande={operation as never}
          user={user as never}
          techNotes={techNotes}
          calculateFinancialScore={calculateFinancialScore as never}
          getNoteGlobale={getNoteGlobale}
          prestaId={Number(commandeId)}
        />,
      ).toBlob();

      const orgName =
        commandes.find((c) => String(c.id) === String(commandeId))?.contrat?.entite?.nom || 'candidat';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `offre-${orgName}-${operation.numero ?? operation.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [operation, techNotes, user, commandes],
  );
  const isConsultation = isConsultationEnCours(statusId);
  const viewOnly = isAnalyseViewOnly(operation);
  // Hors viewOnly, la barre porte « Clôturer l'analyse » (orange) : elle est le CTA de l'etape.
  const analyseCta = analyseNextTab(operation, !viewOnly);

  // Verrou des sections d'analyse : consultation en cours, ou "a analyser" avant ouverture des plis.
  const forceDechiffrage = statusId === StatusId.AAnalyser && !plisOuverts;
  const sectionLocked = isConsultation || forceDechiffrage;
  const sectionHint = isConsultation
    ? 'Disponible à la clôture de la consultation'
    : 'Cliquez sur « Déchiffrer et ouvrir les plis »';
  const dechiffrerDisabled = !forceDechiffrage || dechiffrageLoading;

  // Remonte une erreur de dechiffrage dans la modale d'erreur (le hook expose error/loading).
  useEffect(() => {
    if (dechiffrageError) setErrorModal(dechiffrageError);
  }, [dechiffrageError]);
  const dechiffrerHint = isConsultation
    ? 'Disponible à la clôture de la consultation.'
    : plisOuverts
    ? 'Plis déjà ouverts.'
    : undefined;

  const handleDechiffrer = useCallback(async () => {
    await dechiffrer();
    setAnalyseOpen(true);
    setFinanciereOpen(true);
    setSyntheseOpen(true);
  }, [dechiffrer]);

  const handleSaveAndQuit = useCallback(async () => {
    const validation = validateNotation(commandes, techNotes);
    if (!validation.valid) {
      setNotationErrors(validation.errors);
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const winnerId = resolveWinnerId(operation, techNotes);
      await saveNotesTechniques(operation.id, buildNotePayload(operation, techNotes, winnerId));
      navigate('/operations');
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Une erreur est survenue lors de l'enregistrement.");
    } finally {
      setIsSaving(false);
    }
  }, [commandes, techNotes, operation, navigate]);

  const handleClotureClick = useCallback(() => {
    const validation = validateNotation(commandes, techNotes);
    if (!validation.valid) {
      setNotationErrors(validation.errors);
      return;
    }
    setIsClotureOpen(true);
  }, [commandes, techNotes]);

  const handleClotureConfirm = useCallback(async () => {
    // Pas de choix de signataire a la cloture : le signataire designe (auto) part dans le
    // payload, et l'identite est verifiee plus tard a la signature (nom dans la popup OTP).
    setIsClosing(true);
    try {
      const winnerId = resolveWinnerId(operation, techNotes);
      const res = await finaliserAnalyse(
        operation.id,
        buildValidatePayload(operation, techNotes, winnerId, selection),
      );
      if (res.id_commande) {
        setOperation((prev) => (prev ? { ...prev, id_commande: res.id_commande } : prev));
      }
      setIsClotureOpen(false);
      setIsSuccessOpen(true);
    } catch (e) {
      setIsClotureOpen(false);
      setErrorModal(e instanceof Error ? e.message : "Une erreur est survenue lors de la clôture de l'analyse.");
    } finally {
      setIsClosing(false);
    }
  }, [selection, operation, techNotes, setOperation]);

  const handleAnnuler = useCallback(async () => {
    setIsAnnuling(true);
    try {
      await annulerOperation(operation.id);
      navigate('/operations');
    } catch (e) {
      setErrorModal(e instanceof Error ? e.message : "Erreur lors de l'annulation de l'opération.");
    } finally {
      setIsAnnuling(false);
      setIsAnnulerOpen(false);
    }
  }, [operation.id, navigate]);

  // Apres cloture reussie : refetch (statut -> 13) + bascule sur l'onglet suivant
  // "Notifications et signatures" (ou se fait la signature).
  const handleSuccessContinue = useCallback(() => {
    setIsSuccessOpen(false);
    refetch();
    navigate({ search: '?tab=notifications' });
    scrollToTop();
  }, [refetch, navigate]);

  const cloturee = isAnalyseCloturee(statusId) && isAnalyzed(operation.historique);

  return (
    <div className="opms__analyse">
      {/* Section 1 — Reception des plis */}
      <OperationAnalyseAccordion title="Réception des plis" open={recOpen} onToggle={setRecOpen}>
        <OperationAnalyseReception
          operation={operation}
          isConsultation={isConsultation}
          dechiffrage={dechiffrageInfo}
          onDechiffrer={handleDechiffrer}
          dechiffrerDisabled={dechiffrerDisabled}
          dechiffrerHint={dechiffrerHint}
          onGoToQuestionsReponses={() => navigate(`?tab=questions_reponses`)}
        />
      </OperationAnalyseAccordion>

      {/* Section 2 — Contenu des plis : entete verrouillee mais corps visible (lockedKeepOpen) */}
      <OperationAnalyseAccordion
        title="Contenu des plis"
        locked={sectionLocked}
        lockedKeepOpen
        lockedHint={sectionHint}
        open={contenuOpen}
        onToggle={setContenuOpen}
      >
        <OperationConsultationSuivi
          commandes={commandes}
          lotTechnique={operation.id_lot_technique}
          departement={operation.lot_departement?.id_departement}
          depotParCommande={getDepotParCommande(operation.historique)}
          refusParCommande={getRefusParCommande(operation.historique)}
          plisOuverts={plisOuverts}
          onPrintOffre={handlePrintOffre}
        />
      </OperationAnalyseAccordion>

      {/* Section 3 — Analyse des offres techniques. Bouton IA en haut de la section (une seule
          analyse, possible une fois les plis ouverts et tant que l'analyse n'est pas en lecture seule). */}
      <OperationAnalyseAccordion
        title="Analyse des offres techniques"
        locked={sectionLocked}
        lockedHint={sectionHint}
        open={analyseOpen}
        onToggle={setAnalyseOpen}
      >
        {!sectionLocked && (
          <>
            {!viewOnly && <OperationAnalyseIaButton etat={iaEtat} onLancer={lancerIa} />}
            <OperationAnalyseTechnique
              operation={operation}
              techNotes={techNotes}
              setNote={setNote}
              errors={notationErrors}
              viewOnly={viewOnly}
              iaEnCours={iaEtat === 'en_cours'}
            />
          </>
        )}
      </OperationAnalyseAccordion>

      <div className="page-break" />

      {/* Section 4 — Analyse de l'offre financiere */}
      <OperationAnalyseAccordion
        title="Analyse de l'offre financière"
        locked={sectionLocked}
        lockedHint={sectionHint}
        open={financiereOpen}
        onToggle={setFinanciereOpen}
      >
        {!sectionLocked && <OperationAnalyseFinanciere operation={operation} />}
      </OperationAnalyseAccordion>

      <div className="page-break" />

      {/* Section 5 — Synthese de l'analyse des offres */}
      <OperationAnalyseAccordion
        title="Synthèse de l'analyse des offres"
        locked={sectionLocked}
        lockedHint={sectionHint}
        open={syntheseOpen}
        onToggle={setSyntheseOpen}
      >
        {!sectionLocked && (
          <>
            <OperationAnalyseSynthese operation={operation} techNotes={techNotes} iaEnCours={iaEtat === 'en_cours'} />
            {cloturee && (
              <div className="opms__analyse-info">
                <p>Analyse clôturée — il convient maintenant de notifier les décisions et signer le marché.</p>
              </div>
            )}
          </>
        )}
      </OperationAnalyseAccordion>

      {/* Rappel de la banniere d'etat juste avant les boutons (no-print : deja en tete de page). */}
      <div className="no-print"><OperationStatusBanner operation={operation} context="analyse" /></div>

      {/* Boutons d'action */}
      <div className="action-buttons opms__actions no-print">
        {saveError && <div className="opms__form-error opms__actions-error">{saveError}</div>}

        {canAnnulerOperation(statusId) && (
          <Button
            value={isAnnuling ? 'Annulation...' : 'Annuler la consultation'}
            variant="cancel"
            onClick={() => setIsAnnulerOpen(true)}
            disabled={isAnnuling}
          />
        )}

        {!viewOnly && (
          <Button
            value={isSaving ? 'Enregistrement...' : 'Enregistrer et quitter'}
            variant="secondary"
            onClick={handleSaveAndQuit}
            disabled={isSaving}
          />
        )}

        <PrintButton
          value="Imprimer l'analyse"
          targetRef={printRef}
          disabled={sectionLocked}
          fileName={`${operation.numero ?? ''} - Analyse`}
          suiviHeaderProps={{
            titre: operation.nom || '',
            sousTitre: '',
            subtitle: `${operation.lot_technique?.id ?? ''} - ${operation.lot_technique?.nom ?? ''}`,
            status: operation.status?.nom || 'Statut inconnu',
            statusColor: statusColorById(operation.status?.id),
            delais: operation.expire_at,
          }}
        />

        {!viewOnly && (
          <Button
            value={isClosing ? "Clôture de l'analyse en cours" : "Clôturer l'analyse et préparer le marché"}
            variant="primary"
            disabled={isClosing || sectionLocked}
            onClick={handleClotureClick}
          />
        )}

        {/* Analyse close : le CTA prend la place laissee par « Clôturer l'analyse ». La modale de
            succes post-cloture mene au meme onglet, mais elle ne passe qu'une fois — ce bouton est
            le chemin permanent pour qui revient sur l'onglet plus tard. */}
        <NextTabButton
          label={analyseCta.label}
          hidden={analyseCta.hidden}
          onClick={() => navigate({ search: `?tab=${analyseCta.target}` })}
        />
      </div>

      {/* Modale d'annulation */}
      <ValidationPopUp
        isOpen={isAnnulerOpen}
        title="Annulation de la consultation"
        text="Êtes-vous sûr de vouloir annuler cette consultation ? L'opération et les commandes liées passeront en statut annulé. Cette action est irréversible."
        onClose={() => setIsAnnulerOpen(false)}
        onConfirm={handleAnnuler}
        cancelLabel="Non"
        confirmLabel="Oui, annuler"
        isLoading={isAnnuling}
      />

      {/* Modale de cloture : simple confirmation. Le signataire n'est PAS choisi ici — son nom
          est verifie a la signature (popup OTP de l'onglet Notifications et signatures). */}
      <ValidationPopUp
        isOpen={isClotureOpen}
        title="Clôture de l'analyse"
        text="Vous vous apprêtez à clôturer votre analyse. Il ne sera plus possible de la modifier après cette étape. Souhaitez-vous continuer ?"
        onClose={() => setIsClotureOpen(false)}
        onConfirm={handleClotureConfirm}
        cancelLabel="Annuler"
        confirmLabel={isClosing ? 'Clôture...' : 'Clôturer'}
        isLoading={isClosing}
      />

      {/* Modale de succes */}
      <ValidationPopUp
        isOpen={isSuccessOpen}
        title="Analyse clôturée"
        text="Analyse terminée. Il convient maintenant de notifier les décisions et signer le marché depuis l'onglet « Notifications et signatures »."
        onClose={handleSuccessContinue}
        onConfirm={handleSuccessContinue}
        confirmLabel="Continuer"
      />

      {/* Modale d'erreur */}
      <ValidationPopUp
        isOpen={!!errorModal}
        title="Erreur"
        text={errorModal || ''}
        onClose={() => setErrorModal(null)}
        onConfirm={() => setErrorModal(null)}
        confirmLabel="Fermer"
      />
    </div>
  );
};

export default OperationAnalyse;
